<script setup lang="ts">
  // imports

  import { ref, reactive, onMounted, getCurrentInstance } from 'vue'
  import _ from 'lodash'
  import $sc from '~/plugins/shortcut.collection'
  import dialog from '~/plugins/nikan-dialog-plugin'
  import { post } from '~/plugins/nikan-api'
  import Endpoints from '@nikan/endpoints'
  import moment from 'jalali-moment'
  import { PrintDataConvertTypes } from '@/plugins/stimul'
  import TourModify from './tour-modify.vue'
  import { TourStatus, TimeRange } from '@/enums/capillary'
  import List from '~/views/capillary/tour-definition/visit-tour/list.vue'
  import Filters from './filters.vue'

  // types

  type Props = {
    data?: Array<any>,
    options?: any,
  }

  // props

  const props = defineProps<Props>()

  const _instance = getCurrentInstance()!
  const vm = () => _instance.proxy as any

  // state

  const filterData = ref({
    tourStatus: TourStatus.All,
    timeRanges: null as any
  })

  const settings = ref<any>({})
  const toursList = ref<any[]>([])

  const pageButtons = reactive({
    addButton: {
      title: '',
      shortcut: $sc.find('new'),
      icon: 'plus',
      isPrimary: false,
      visible: true,
      disabled: false,
      handle: () => {
        vm().showModal({
          component: TourModify,
          options: {
            type: settings.value.type
          }
        }).then((res: any) => {
          if (res && res.success) getToursList()
        }).catch(() => { })
      },
      policyClaims: () => '',
    },
    printButton: {
      title: '',
      shortcut: $sc.find('print'),
      visible: true,
      icon: 'print',
      handle: () => {
        vm().print_startPrint?.(print_dataSources())
      },
      policyClaims: () => '',
    }
  })

  const refreshButton = reactive({
    title: '',
    icon: 'sync',
    handle: () => {
      getToursList()
    },
  })

  const componentOptions = reactive({
    applyFilterBtn: {
      title: '',
      shortcut: $sc.find('search'),
      isPrimary: false,
      icon: ''
    },
    tourStatus: {
      searchable: true,
      required: true,
      remoteSearch: false,
      placeholder: '',
      display: (item: any) => vm().$t(`enum-items.TourStatus.${item}`),
      searchableFields: ['key'],
    },
    timeRange: {
      searchable: true,
      required: true,
      remoteSearch: false,
      placeholder: '',
      display: (item: any) => vm().$t(`enum-items.TimeRange.${item}`),
      searchableFields: ['key'],
    },
  })

  // methods

  const getTimeRangeFilter = () => {
    if (!filterData.value.timeRanges) {
      return {
        fromDate: null,
        toDate: null,
      }
    }

    const now = moment()
    let fromDate = null
    let toDate = null

    switch (filterData.value.timeRanges) {
      case TimeRange.Today:
        fromDate = now.clone().locale('fa').startOf('day').locale('en').format('YYYY-MM-DDTHH:mm:ss')
        toDate = now.clone().locale('fa').endOf('day').locale('en').format('YYYY-MM-DDTHH:mm:ss')
        break

      case TimeRange.CurrentWeek:
        fromDate = now.clone().locale('fa').startOf('week').locale('en').format('YYYY-MM-DDTHH:mm:ss')
        toDate = now.clone().locale('fa').endOf('week').locale('en').format('YYYY-MM-DDTHH:mm:ss')
        break

      case TimeRange.CurrentMonth:
        fromDate = now.clone().locale('fa').startOf('jMonth').locale('en').format('YYYY-MM-DDTHH:mm:ss')
        toDate = now.clone().locale('fa').endOf('jMonth').locale('en').format('YYYY-MM-DDTHH:mm:ss')
        break
    }

    return { fromDate, toDate }
  }

  const getStatusFilter = () => {
    switch (filterData.value.tourStatus) {
      case TourStatus.Active:
        return true

      case TourStatus.InActive:
        return false

      case TourStatus.All:
      default:
        return null
    }
  }

  const getToursList = () => {
    const v = vm()
    const wait = v.$wait(v.$el)

    const { fromDate, toDate } = getTimeRangeFilter()

    const data = {
      type: settings.value.type ?? null,
      isActive: getStatusFilter(),
      fromDate,
      toDate,
    }

    post(Endpoints.getUrl('tour.list'), data)
      .then((res: any) => {
        toursList.value = (res.data ?? []).map((item: any) => ({
          ...item,
          details: item.visitTourDetails || item.marketingTourDetails || item.details || []
        }))
      })
      .finally(() => {
        wait.end()
      })
  }

  const editTour = (item: any) => {
    const v = vm()
    const wait = v.$wait(v.$el)
    let previousFocus = document.activeElement as HTMLElement

    post(Endpoints.getUrl('tour.get'), { tourHeaderId: item.id })
      .then((res: any) => {
        const fullItem = {
          ...(res.data || item),
          details: res.data?.visitTourDetails || res.data?.marketingTourDetails || res.data?.details || []
        }
        v.showModal({
          component: TourModify,
          data: fullItem,
          options: {
            type: settings.value.type
          }
        }).then(() => {
          getToursList()
          setTimeout(() => {
            if (previousFocus && document.contains(previousFocus)) {
              previousFocus.focus()
            }
          }, 300)
        }).catch(() => { 
          setTimeout(() => {
            if (previousFocus && document.contains(previousFocus)) {
              previousFocus.focus()
            }
          }, 100)
        })
      })
      .finally(() => {
        wait.end()
      })
  }

  const deleteTour = (item: any) => {
    const v = vm()
    post(Endpoints.getUrl('tour.delete'), { tourHeaderId: item.id }).then(() => {
      getToursList()
      v.$toast(v.$t('capillary.tour.m.delete.success', { code: item.code }), { type: 'success' })
    })
  }

  const showDeleteDialog = (item: any) => {
    const v = vm()
    let previousFocus = document.activeElement as HTMLElement
    if (_.isEmpty(item)) {
      v.$toast(v.$t('capillary.tour.m.select-one-row'), { type: 'info' })
      return
    }
    dialog({
      title: v.$t('capillary.tour.m.delete.title'),
      body: v.$t('capillary.tour.m.delete.want-to-delete', {
        code: item.code,
      }),
      type: 'warning',
      autoClose: true,
      buttons: [
        {
          title: v.$t('dialog.yes'),
          shortcut: $sc.find('yes', 'dialog'),
          isPrimary: true,
          handle: () => {
            deleteTour(item)
          },
        },
        {
          title: v.$t('dialog.cancel'),
          shortcut: $sc.find('cancel', 'dialog'),
          isCancel: true,
          handle: () => { 
            setTimeout(() => {
              if (previousFocus && document.contains(previousFocus)) {
                previousFocus.focus()
              }
            }, 100)
          },
        },
      ],
    })
  }

  const print_dataSources = () => {
    const $t = vm().$t
    const data = toursList.value
    return [
      {
        name: 'tours-list',
        data: data,
        translatedDataSetName: $t('capillary.tour.list'),
        translatePrefix: 'capillary.tour.f',
        converter: {
          type: PrintDataConvertTypes.MAPPER,
          settings: {
            code: 'code',
            name: 'name',
            startDate: 'startDate',
            endDate: 'endDate',
            line: (item: any, row: any) => row?.line?.name ?? '',
            role: 'role',
            status: (item: any, row: any) => row.isActive ? $t('capillary.tour.f.opened') : $t('capillary.tour.f.closed')
          }
        },
      }
    ]
  }

  // life-cycles

  onMounted(() => {
    const v = vm()
    const $t = v.$t.bind(v)

    settings.value = _.merge({}, settings.value, props.options)

    componentOptions.applyFilterBtn.title = $t('capillary.tour.buttons.applyFilter')
    componentOptions.tourStatus.placeholder = $t('select-it')
    componentOptions.timeRange.placeholder = $t('select-it')

    pageButtons.printButton.title = $t('capillary.tour.buttons.printTours')
    pageButtons.addButton.title = $t('capillary.tour.buttons.addTour')
    refreshButton.title = $t('trs.chq.oper.refresh')

    const buttonsArray = [
      pageButtons.addButton,
      { type: 'seprator' },
      pageButtons.printButton
    ]

    const operation = $t(`capillary.tour.types.${settings.value.type || ''}.activity`)
    v.prepareWindow($t('capillary.tour.title', { operation }), buttonsArray)

    getToursList()
  })

</script>

<template>
  <nk-page commercialAccess="" :policyClaims="[]" :buttons="[refreshButton]">
    <template #badges>
      <nk-badge system="capillary">
        <nk-badge-item type="system" modelValue="capillary"></nk-badge-item>
        <nk-badge-item type="system" modelValue="capillary" :title="$t('capillary.tour.modify')"></nk-badge-item>
        <nk-badge-item type="system" modelValue="capillary" :title="$t('capillary.tour.title', { operation: $t(`capillary.tour.types.${settings?.type ?? ''}.activity`) })"></nk-badge-item>
      </nk-badge>
    </template>
    <div class="h-100 d-flex flex-column form-container overflow-auto">
      <Filters v-model="filterData" :tour-type="settings?.type ?? 'Marketing'" @change="getToursList" />
      <div class="h-grow-lock pt-2">
        <div class="w-100 h-100">
          <List :items="toursList" :tour-type="settings?.type ?? 'Marketing'" @edit="editTour" @delete="showDeleteDialog" />
        </div>
      </div>
    </div>
  </nk-page>
</template>

<style scoped>
  .btn-create {
    background-color: #2e4381;
    color: #fff;
    border: none;
    border-radius: 6px;
    padding: 8px 20px;
    font-size: 12px;
    font-weight: 500;
    transition: background-color 0.2s ease;
    cursor: pointer;
  }

  .btn-create:hover {
    background-color: #223261;
  }
</style>
